// EmployerProfileSummary — compact identity/context card for the employer Home
// right rail: who you are + which unit/organisation you manage. Counts live in
// the Home snapshot, so this stays identity-focused.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, Building2, ArrowRight } from 'lucide-react';
import { OrgLogo } from '@/components/custom-ui/OrgLogo';
import { useAuth } from '@/contexts/AuthContext';
import { useEmployer } from '@/contexts/EmployerContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';

// `leading` (#354) lets the Organisation row show the org's actual logo instead
// of the generic Building2 glyph. Enhet and Ort keep their glyphs — a unit and a
// city have no identity of their own to show, an organisation does.
const Fact: React.FC<{
    icon: typeof Building;
    label: string;
    value: string;
    leading?: React.ReactNode;
}> = ({ icon: Icon, label, value, leading }) => (
    <div className="flex items-center gap-2.5">
        {leading ?? <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />}
        <span className="text-sm text-gray-500">{label}</span>
        <span className="text-sm font-medium text-gray-900 ml-auto truncate">{value}</span>
    </div>
);

const EmployerProfileSummary: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { employerDetails } = useEmployer();

    const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'Min profil';
    const initials = ((user?.first_name?.[0] ?? '') + (user?.last_name?.[0] ?? '')).toUpperCase() || (user?.email?.[0] ?? '?').toUpperCase();
    const jobTitle = employerDetails?.employer?.job_title;
    const unit = employerDetails?.unit;
    const org = employerDetails?.organisation;

    return (
        <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-5`}>
            <div className="flex items-center gap-3">
                <Avatar className="w-12 h-12 ring-2 ring-white shadow-sm">
                    <AvatarImage src={resolveMediaUrl(user?.picture_url)} alt={name} />
                    <AvatarFallback className="bg-[#1A4D4F] text-[#f5f5f5] font-semibold">{initials}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{name}</p>
                    <p className="text-sm text-gray-500 truncate">{jobTitle || 'Arbetsgivare'}</p>
                </div>
            </div>

            <div className="mt-4 space-y-2.5 border-t border-gray-100 pt-4">
                <Fact icon={Building} label="Enhet" value={unit?.name || '—'} />
                <Fact
                    icon={Building2}
                    label="Organisation"
                    value={org?.name || '—'}
                    leading={org ? <OrgLogo name={org.name} logoUrl={org.logo_url} size="xs" /> : undefined}
                />
                {unit?.city && <Fact icon={Building} label="Ort" value={unit.city} />}
            </div>

            <button
                onClick={() => navigate('/dashboard/profile')}
                className="mt-4 w-full flex items-center justify-center gap-1.5 text-sm font-medium text-[#1A4D4F] hover:bg-[#A4C3B2]/20 rounded-xl py-2 transition-colors"
            >
                Min profil <ArrowRight className="w-4 h-4" />
            </button>
        </div>
    );
};

export default EmployerProfileSummary;
